import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, MapPin, HardHat, CalendarClock, CheckCircle2, XCircle, Undo2, Lock, Play, Hammer, Link2, Copy, Mail, Pencil, Trash2,
  MessageSquare, History, Camera, ShieldCheck, AlertTriangle, FileSignature, CalendarPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AuthImage } from "@/components/common/AuthImage";
import { DiaryPhotosEditor } from "@/components/diary/DiaryPhotosEditor";
import { BookTradeCard, BOOKING_STATUS_META } from "@/components/defects/BookTradeCard";
import { api, errMsg } from "@/lib/api";
import { isAdminLike } from "@/lib/roles";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { DEFECT_STATUS_META, DEFECT_PRIORITY_META, HISTORY_META, fmtHistoryMeta } from "@/components/defects/defectMeta";

const Field = ({ icon: Icon, label, children, testId }) => (
  <div className="flex items-start gap-2" data-testid={testId}>
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  </div>
);

/**
 * One defect: details · photos (raised + rectification evidence) · sign-off history · role-based actions.
 * Staff: start / mark rectified (on behalf of a trade) · verifier/admin: verify / reject / close / reopen · copy or email the trade link.
 */
export const DefectDetailDialog = ({ open, onOpenChange, defectId, closeReasons = [], onChanged, onEdit, onPhotoOpen }) => {
  const { user } = useAuth();
  const isAdmin = isAdminLike(user); // Phase 191 — read-only viewers see what admins see
  const isContractor = user?.role === "contractor";
  const [d, setD] = useState(null);
  const [panel, setPanel] = useState(""); // reject | close | reopen | rectify | comment
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => {
    if (!defectId) return;
    api.get(`/defects/${defectId}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
  };
  useEffect(() => { if (open) { setD(null); setPanel(""); setComment(""); setReason(""); setPhotos([]); load(); } }, [open, defectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action, extra = {}) => {
    setBusy(action);
    try {
      const body = { action, comment: comment.trim(), reason, ...extra };
      if (action === "rectify") body.photos = photos.filter((p) => p.kind === "new").map((p) => ({ b64: p.b64, caption: p.caption || "" }));
      const res = await api.post(`/defects/${d.id}/transition`, body);
      const msg = {
        start: "Marked in progress", rectify: "Marked rectified — the verifier has been alerted", verify: `${d.number} verified and closed`,
        reject: "Sent back to the trade with your comment", close: `${d.number} closed`, reopen: `${d.number} reopened`, comment: "Comment added",
      }[action];
      toast.success(msg || "Done");
      setD(res.data);
      setPanel(""); setComment(""); setReason(""); setPhotos([]);
      onChanged?.(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(d.trade_link); toast.success("Secure trade link copied"); } catch { toast.info(d.trade_link); }
  };

  const emailTrade = async () => {
    setBusy("email");
    try {
      const res = await api.post(`/jobs/${d.job_id}/defects/notify-trade`, { ids: [d.id], attach_pdf: false });
      if (res.data.sent) toast.success(`Emailed ${res.data.recipients?.join(", ")}`);
      else toast.warning(res.data.error || "Could not email the trade — check the email service and the contractor's address");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const del = async () => {
    setBusy("delete");
    try {
      await api.delete(`/defects/${d.id}`);
      toast.success(`${d.number} deleted`);
      onChanged?.(null);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
      setConfirmDelete(false);
    }
  };

  const sm = d ? DEFECT_STATUS_META[d.status] || DEFECT_STATUS_META.open : null;
  const pm = d ? DEFECT_PRIORITY_META[d.priority] || DEFECT_PRIORITY_META.major : null;
  const canVerify = Boolean(d?.can_verify);
  const canClose = isAdmin || canVerify;
  const raisedPhotos = (d?.photos || []).filter((p) => p.kind !== "rectified");
  const fixPhotos = (d?.photos || []).filter((p) => p.kind === "rectified");

  const PhotoGrid = ({ items, prefix }) => (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {items.map((p, i) => (
        <button key={p.name} type="button" className="group text-left" onClick={() => onPhotoOpen?.({ path: `/defects/${d.id}/photos/${p.name}`, caption: p.caption })} data-testid={`${prefix}-${i}`}>
          <AuthImage path={`/defects/${d.id}/photos/${p.name}`} alt={p.caption || `Photo ${i + 1}`} className="h-24 w-full rounded-lg border border-border object-cover transition-transform duration-150 group-hover:scale-[1.01]" testId={`${prefix}-img-${i}`} />
          {p.caption && <p className="mt-1 truncate text-[10px] text-muted-foreground">{p.caption}</p>}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="defect-detail-dialog">
        {!d ? (
          <div className="py-16 text-center text-sm text-muted-foreground" data-testid="defect-detail-loading">Loading defect…</div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex flex-wrap items-start justify-between gap-3 pr-6">
                <div className="min-w-0">
                  <DialogTitle className="flex flex-wrap items-center gap-2 font-heading">
                    <ClipboardList className="h-5 w-5 text-primary" />
                    <span data-testid="defect-detail-number">{d.number}</span>
                    <Badge variant="outline" className={`font-normal ${pm.cls}`} data-testid="defect-detail-priority">{pm.label}</Badge>
                    <Badge variant="outline" className={`font-normal ${sm.cls}`} data-testid="defect-detail-status">{sm.label}</Badge>
                    {d.overdue && <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 font-normal text-rose-300" data-testid="defect-detail-overdue">Overdue</Badge>}
                  </DialogTitle>
                  <DialogDescription className="mt-1" data-testid="defect-detail-description">{d.description}</DialogDescription>
                </div>
                <div className="flex gap-1">
                  {!isContractor && (d.status === "open" || isAdmin) && (
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => onEdit?.(d)} data-testid="defect-detail-edit"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                  )}
                  {!isContractor && (d.status === "open" || isAdmin) && (
                    <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-destructive" onClick={() => setConfirmDelete(true)} data-testid="defect-detail-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </div>
            </DialogHeader>

            {d.status === "rectified" && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs" data-testid="defect-detail-rectified-banner">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" />
                <span><strong>{d.rectified_by || "The trade"}</strong> marked this rectified on {fmtDateTime(d.rectified_at)}{d.rectified_note ? ` — “${d.rectified_note}”` : ""}. {canVerify ? "Inspect it, then verify or reject below." : `Waiting for ${d.verifier?.name || "the verifier"} to inspect.`}</span>
              </div>
            )}
            {d.status === "verified" && (
              <div className="flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs" data-testid="defect-detail-verified-banner">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-300" />
                <span>Verified by <strong>{d.verified_by}</strong> on {fmtDateTime(d.verified_at)}{d.verified_note ? ` — “${d.verified_note}”` : ""}. This defect counts as closed.</span>
              </div>
            )}
            {d.status === "closed" && (
              <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="defect-detail-closed-banner">
                <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span>Closed by <strong>{d.closed_by}</strong> on {fmtDateTime(d.closed_at)} — {closeReasons.find((r) => r.value === d.close_reason)?.label || d.close_reason}{d.close_note ? `: “${d.close_note}”` : ""}.</span>
              </div>
            )}
            {d.last_rejection?.comment && d.status !== "verified" && d.status !== "closed" && (
              <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs" data-testid="defect-detail-rejection-banner">
                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-300" />
                <span>Last rejection by <strong>{d.last_rejection.by}</strong> ({fmtDateTime(d.last_rejection.at)}): “{d.last_rejection.comment}” · rejected {d.reject_count}×</span>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field icon={MapPin} label="Location" testId="defect-detail-location">{d.location_label}</Field>
              <Field icon={HardHat} label="Trade responsible" testId="defect-detail-trade">
                {d.contractor_name ? (<>{d.contractor_name}<span className="block text-[11px] text-muted-foreground">{[d.trade, d.contractor_company].filter(Boolean).join(" · ")}{d.contractor_email ? ` · ${d.contractor_email}` : ""}</span></>) : (d.trade || "Unassigned")}
              </Field>
              <Field icon={CalendarClock} label="Raised / due" testId="defect-detail-dates">{fmtDate(d.date_raised)} → <span className={d.overdue ? "text-rose-300" : ""}>{d.due_date ? fmtDate(d.due_date) : "—"}</span>{d.days_open != null && <span className="block text-[11px] text-muted-foreground">open {d.days_open} day{d.days_open === 1 ? "" : "s"} · raised by {d.raised_by}{d.source === "client" ? " via the client portal" : ""}</span>}</Field>
              <Field icon={ShieldCheck} label="Verifier" testId="defect-detail-verifier">{d.verifier?.name || "—"}{d.trade_email_status && <span className="block text-[11px] text-muted-foreground">Trade email: {d.trade_email_status}{d.trade_notified_at ? ` · ${fmtDate(d.trade_notified_at)}` : ""}</span>}</Field>
            </div>

            {d.notes && <p className="rounded-lg bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="defect-detail-notes"><strong className="text-foreground">Internal note:</strong> {d.notes}</p>}

            {(raisedPhotos.length > 0 || fixPhotos.length > 0) && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"><Camera className="h-3 w-3" /> As raised ({raisedPhotos.length})</p>
                  {raisedPhotos.length ? <PhotoGrid items={raisedPhotos} prefix="defect-photo-raised" /> : <p className="text-xs text-muted-foreground">No photos</p>}
                </div>
                <div>
                  <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"><Hammer className="h-3 w-3" /> Rectification evidence ({fixPhotos.length})</p>
                  {fixPhotos.length ? <PhotoGrid items={fixPhotos} prefix="defect-photo-fixed" /> : <p className="text-xs text-muted-foreground">None yet</p>}
                </div>
              </div>
            )}

            {/* formal notice (Phase 88) */}
            {d.last_notice?.number && !isContractor && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2 text-xs" data-testid="defect-detail-notice">
                <FileSignature className="h-3.5 w-3.5 text-orange-300" />
                <span>
                  Formal defect notice <strong>{d.last_notice.number}</strong> {d.last_notice.sent ? "issued" : "drafted"} {fmtDate(d.last_notice.at)} · respond by <strong>{fmtDate(d.last_notice.response_due)}</strong>
                  {d.notice_count > 1 ? ` · ${d.notice_count} notices in total` : ""}
                </span>
                <Link to={`/notices?job=${d.job_id}&open=${d.last_notice.id}`} className="ml-auto font-medium text-primary hover:underline" data-testid="defect-detail-notice-link">Open notice</Link>
              </div>
            )}

            {/* trade bookings (Phase 91A) */}
            {(d.bookings || []).length > 0 && (
              <div className="space-y-1 rounded-lg border border-primary/30 bg-accent/40 px-3 py-2 text-xs" data-testid="defect-detail-bookings">
                {d.bookings.map((b) => {
                  const bm = BOOKING_STATUS_META[b.status] || { label: b.status, cls: "border-border bg-muted/40 text-muted-foreground" };
                  return (
                    <div key={b.id} className="flex flex-wrap items-center gap-2" data-testid={`defect-booking-${b.id}`}>
                      <CalendarPlus className="h-3.5 w-3.5 text-primary" />
                      <span>Trade booked <strong>{fmtDate(b.start_date)}</strong>{b.end_date !== b.start_date ? ` → ${fmtDate(b.end_date)}` : ""}</span>
                      <Badge variant="outline" className={`h-5 font-normal ${bm.cls}`} data-testid={`defect-booking-status-${b.id}`}>{bm.label}</Badge>
                      {!isContractor && <Link to={`/schedule?job=${d.job_id}`} className="ml-auto font-medium text-primary hover:underline" data-testid={`defect-booking-link-${b.id}`}>Open calendar</Link>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* trade link */}
            {d.trade_link && !d.resolved && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2" data-testid="defect-detail-link-box">
                <Link2 className="h-3.5 w-3.5 text-primary" />
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground" data-testid="defect-detail-trade-link">{d.trade_link}</span>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={copyLink} data-testid="defect-detail-copy-link"><Copy className="mr-1 h-3 w-3" /> Copy</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={emailTrade} disabled={!!busy || !d.contractor_email} title={d.contractor_email ? `Email ${d.contractor_email}` : "No trade email on file"} data-testid="defect-detail-email-trade"><Mail className="mr-1 h-3 w-3" /> {busy === "email" ? "Sending…" : "Email trade"}</Button>
              </div>
            )}

            {/* actions */}
            <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3" data-testid="defect-detail-actions">
              {d.status === "open" && <Button variant="outline" size="sm" onClick={() => act("start")} disabled={!!busy} data-testid="defect-action-start"><Play className="mr-1.5 h-3.5 w-3.5" /> Mark in progress</Button>}
              {(d.status === "open" || d.status === "in_progress") && <Button variant="outline" size="sm" onClick={() => setPanel(panel === "rectify" ? "" : "rectify")} disabled={!!busy} data-testid="defect-action-rectify"><Hammer className="mr-1.5 h-3.5 w-3.5" /> Mark rectified</Button>}
              {d.status === "rectified" && canVerify && (
                <>
                  <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-600/90" onClick={() => act("verify")} disabled={!!busy} data-testid="defect-action-verify"><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> {busy === "verify" ? "Verifying…" : "Verify — closes defect"}</Button>
                  <Button variant="outline" size="sm" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" onClick={() => setPanel(panel === "reject" ? "" : "reject")} disabled={!!busy} data-testid="defect-action-reject"><XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject</Button>
                </>
              )}
              {!d.resolved && canClose && <Button variant="ghost" size="sm" onClick={() => setPanel(panel === "close" ? "" : "close")} disabled={!!busy} data-testid="defect-action-close"><Lock className="mr-1.5 h-3.5 w-3.5" /> Close without rectification</Button>}
              {d.resolved && canClose && <Button variant="outline" size="sm" onClick={() => setPanel(panel === "reopen" ? "" : "reopen")} disabled={!!busy} data-testid="defect-action-reopen"><Undo2 className="mr-1.5 h-3.5 w-3.5" /> Reopen</Button>}
              {!isContractor && !d.resolved && (
                <Button variant={panel === "book" ? "secondary" : "outline"} size="sm" className="border-primary/40 text-primary hover:bg-accent" onClick={() => setPanel(panel === "book" ? "" : "book")} disabled={!!busy} data-testid="defect-action-book-trade">
                  <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Book trade in
                </Button>
              )}
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setPanel(panel === "comment" ? "" : "comment")} data-testid="defect-action-comment"><MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Comment</Button>
            </div>

            {panel === "book" && (
              <BookTradeCard
                defect={d}
                onBooked={() => { setPanel(""); load(); onChanged?.(d); }}
                onDismiss={() => setPanel("")}
                testPrefix="defect-book"
              />
            )}

            {panel && panel !== "book" && (
              <div className={`space-y-2 rounded-lg border p-3 ${panel === "reject" || panel === "reopen" ? "border-rose-500/30 bg-rose-500/5" : panel === "rectify" ? "border-amber-500/30 bg-amber-500/5" : "border-border"}`} data-testid={`defect-panel-${panel}`}>
                {panel === "close" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Reason</Label>
                    <Select value={reason} onValueChange={setReason}>
                      <SelectTrigger className="h-9" data-testid="defect-close-reason"><SelectValue placeholder="Why is it being closed?" /></SelectTrigger>
                      <SelectContent>{closeReasons.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {panel === "rectify" && (
                  <DiaryPhotosEditor
                    photos={photos}
                    setPhotos={setPhotos}
                    max={Math.max(0, 10 - (d.photos || []).length)}
                    testPrefix="defect-fix-photos"
                    label="Evidence photos"
                    hint="of the finished work · Mark up to point out the fix"
                    markup={{
                      title: `Mark up · ${d.number}`,
                      stampDefaults: {
                        site: d.job_name || "",
                        unit: "",
                        area: [d.area, d.location].filter(Boolean).join(" · "),
                        description: `Rectified: ${d.description || ""}`.trim(),
                        trade: d.contractor_name ? `${d.trade ? `${d.trade} — ` : ""}${d.contractor_name}` : d.trade || "",
                      },
                    }}
                  />
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">{{ reject: "What still needs fixing? (sent to the trade)", close: "Comment", reopen: "Why is it being reopened? (sent to the trade)", rectify: "Note for the verifier", comment: "Comment" }[panel]}</Label>
                  <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="defect-panel-comment" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setPanel(""); setComment(""); }} data-testid="defect-panel-cancel">Cancel</Button>
                  <Button size="sm" variant={panel === "reject" || panel === "reopen" ? "destructive" : "default"} onClick={() => act(panel)} disabled={!!busy} data-testid="defect-panel-confirm">
                    {busy === panel ? "Working…" : { reject: "Reject & send back", close: "Close defect", reopen: "Reopen", rectify: "Mark rectified", comment: "Add comment" }[panel]}
                  </Button>
                </div>
              </div>
            )}

            {/* history */}
            <div data-testid="defect-detail-history">
              <p className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"><History className="h-3 w-3" /> Sign-off history</p>
              <ol className="space-y-1.5 border-l border-border pl-4">
                {[...(d.history || [])].reverse().map((h) => {
                  const hm = HISTORY_META[h.action] || { label: h.action, tone: "text-muted-foreground" };
                  const meta = fmtHistoryMeta(h);
                  return (
                    <li key={h.id} className="relative text-xs" data-testid={`defect-history-${h.action}`}>
                      <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-border" />
                      <span className={`font-medium ${hm.tone}`}>{hm.label}</span>
                      <span className="text-muted-foreground"> · {h.by || "—"}{h.by_kind === "trade" ? " (trade)" : ""} · {fmtDateTime(h.at)}</span>
                      {h.comment && <p className="mt-0.5 text-foreground/90">“{h.comment}”</p>}
                      {meta && <p className="text-[10px] text-muted-foreground">{meta}</p>}
                    </li>
                  );
                })}
              </ol>
            </div>
          </>
        )}

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent data-testid="defect-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {d?.number}?</AlertDialogTitle>
              <AlertDialogDescription>Its photos and sign-off history are removed. Prefer “Close without rectification” to keep the record.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="defect-delete-cancel">Keep it</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={del} data-testid="defect-delete-confirm-btn">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
